import type { DriveArtifactItem, DriveSummaryStrip, DriveWindowMode } from './types';

export interface DriveWindowSelection {
  win: DriveWindowMode;
  periodDiv: 'month' | 'quarter' | 'year';
  periodOff: number;
  dStart: string;
  dEnd: string;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function endOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

export function reportingPeriodBounds(
  periodDiv: DriveWindowSelection['periodDiv'],
  periodOff: number,
  refDate: Date = new Date()
): { start: Date; end: Date } {
  const base = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate());
  if (periodDiv === 'month') {
    const month = base.getMonth() + periodOff;
    const start = new Date(base.getFullYear(), month, 1);
    const monthEnd = new Date(base.getFullYear(), month + 1, 0);
    const end = periodOff === 0 && base < monthEnd ? new Date(base) : monthEnd;
    return { start: startOfDay(start), end: endOfDay(end) };
  }
  if (periodDiv === 'quarter') {
    const q = Math.floor(base.getMonth() / 3) + periodOff;
    const year = base.getFullYear() + Math.floor(q / 4);
    const qMod = ((q % 4) + 4) % 4;
    const start = new Date(year, qMod * 3, 1);
    const end = new Date(year, qMod * 3 + 3, 0);
    return { start: startOfDay(start), end: endOfDay(end) };
  }
  const year = base.getFullYear() + periodOff;
  const start = new Date(year, 0, 1);
  const end = new Date(year, 11, 31);
  return { start: startOfDay(start), end: endOfDay(end) };
}

export function formatReportingPeriodLabel(
  periodDiv: DriveWindowSelection['periodDiv'],
  periodOff: number,
  refDate: Date = new Date()
): string {
  const { start } = reportingPeriodBounds(periodDiv, periodOff, refDate);
  if (periodDiv === 'month') {
    const label = `${MONTH_NAMES[start.getMonth()]} ${start.getFullYear()}`;
    return periodOff === 0 ? `${label} MTD` : label;
  }
  if (periodDiv === 'quarter') {
    const q = Math.floor(start.getMonth() / 3) + 1;
    return `Q${q} ${start.getFullYear()}`;
  }
  return `${start.getFullYear()}`;
}

export function windowBounds(
  state: DriveWindowSelection,
  refDate: Date = new Date()
): { start: Date | null; end: Date | null } {
  if (state.win === 'today') {
    const day = startOfDay(refDate);
    return { start: day, end: endOfDay(day) };
  }
  if (state.win === 'period') {
    const { start, end } = reportingPeriodBounds(state.periodDiv, state.periodOff, refDate);
    return { start, end };
  }
  if (state.win === 'daterange' && state.dStart && state.dEnd) {
    return { start: startOfDay(new Date(state.dStart)), end: endOfDay(new Date(state.dEnd)) };
  }
  return { start: null, end: null };
}

export function isFutureWindowEnd(state: DriveWindowSelection, refDate: Date = new Date()): boolean {
  const { end } = windowBounds(state, refDate);
  if (!end) return false;
  return end.getTime() > endOfDay(refDate).getTime();
}

export function filterArtifactsByWindow(
  artifacts: DriveArtifactItem[],
  state: DriveWindowSelection,
  refDate: Date = new Date()
): DriveArtifactItem[] {
  if (state.win === 'today') return artifacts;
  const { start, end } = windowBounds(state, refDate);
  if (!start || !end) return artifacts;
  return artifacts.filter((artifact) => {
    if (!artifact.statusDate) return true;
    const parsed = Date.parse(artifact.statusDate);
    if (Number.isNaN(parsed)) return true;
    const d = new Date(parsed);
    return d >= start && d <= end;
  });
}

export function deriveDriveSummaryFromArtifacts(artifacts: DriveArtifactItem[]): DriveSummaryStrip {
  return {
    inFlow: artifacts.length,
    movedRecently: artifacts.filter((a) => a.status === 'moved').length,
    blocked: artifacts.filter((a) => a.status === 'blocked').length,
    expectedToMove: artifacts.filter((a) => a.life === 'active').length,
  };
}

export function defaultDateRangeInputs(refDate: Date = new Date()): { dStart: string; dEnd: string } {
  const start = startOfDay(refDate);
  const end = new Date(start);
  end.setDate(end.getDate() + 13);
  return { dStart: toIsoDate(start), dEnd: toIsoDate(end) };
}

/** Month offset of ``YYYY-MM`` (or start date) relative to *refDate*'s month. */
export function periodOffFromReportingPeriodCode(
  codeOrStart: string | null | undefined,
  refDate: Date = new Date()
): number | null {
  const raw = (codeOrStart || '').trim();
  if (!raw) return null;
  const m = raw.match(/(20\d{2})-(\d{2})/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!year || !month || month < 1 || month > 12) return null;
  return (year - refDate.getFullYear()) * 12 + (month - 1 - refDate.getMonth());
}

/** ``YYYY-MM`` for the window currently selected in period mode. */
export function reportingPeriodCodeForWindow(
  state: Pick<DriveWindowSelection, 'periodDiv' | 'periodOff'>,
  refDate: Date = new Date()
): string {
  const { start } = reportingPeriodBounds(state.periodDiv, state.periodOff, refDate);
  const y = start.getFullYear();
  const m = String(start.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
